/**
 * Polygon indices WebSocket (I:SPX, I:NDX, …).
 * Falls back silently when the API key lacks an indices subscription.
 */
import type { ChartStockLiveCallback, ChartStockLiveTick } from "./chartStockLiveTypes";

const INDEX_WS_URL = "wss://socket.polygon.io/indices";
const DEFAULT_KEY =
    (typeof process !== "undefined" &&
        (process.env.REACT_APP_POLYGON_STOCKS_API_KEY ||
            process.env.REACT_APP_POLYGON_CHART_WS_API_KEY ||
            process.env.REACT_APP_POLYGON_API_KEY)) ||
    "";

type IndexAgg = {
    ev: "A" | "AM";
    sym: string;
    o?: number;
    c?: number;
    h?: number;
    l?: number;
    v?: number;
    s?: number;
    e?: number;
};

class PolygonIndexWebSocketService {
    private ws: WebSocket | null = null;
    private callbacks = new Map<string, ChartStockLiveCallback[]>();
    private authenticated = false;
    private connecting = false;
    private readonly apiKey: string;
    private authFailed = false;

    constructor(apiKey: string) {
        this.apiKey = apiKey;
    }

    isAvailable(): boolean {
        return this.apiKey.length >= 10 && !this.authFailed;
    }

    subscribe(polygonIndexSymbol: string, cb: ChartStockLiveCallback): void {
        const sym = polygonIndexSymbol.trim().toUpperCase();
        if (!this.callbacks.has(sym)) this.callbacks.set(sym, []);
        this.callbacks.get(sym)!.push(cb);
        if (this.authFailed) return;
        if (this.ws?.readyState === WebSocket.OPEN && this.authenticated) {
            this.sendSub(sym);
        } else if (this.ws?.readyState !== WebSocket.OPEN) {
            this.connect();
        }
    }

    unsubscribe(polygonIndexSymbol: string, cb: ChartStockLiveCallback): void {
        const sym = polygonIndexSymbol.trim().toUpperCase();
        const list = this.callbacks.get(sym);
        if (!list) return;
        const i = list.indexOf(cb);
        if (i >= 0) list.splice(i, 1);
        if (list.length === 0) this.callbacks.delete(sym);
    }

    private connect(): void {
        if (this.connecting || this.authFailed || !this.apiKey) return;
        this.connecting = true;
        try {
            this.ws = new WebSocket(INDEX_WS_URL);
            this.ws.onopen = () => {
                this.connecting = false;
                this.ws?.send(
                    JSON.stringify({ action: "auth", params: this.apiKey }),
                );
            };
            this.ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(
                        typeof ev.data === "string"
                            ? ev.data
                            : String(ev.data),
                    );
                    this.dispatch(data);
                } catch {
                    /* ignore */
                }
            };
            this.ws.onclose = () => {
                this.connecting = false;
                this.authenticated = false;
            };
            this.ws.onerror = () => {
                this.connecting = false;
            };
        } catch {
            this.connecting = false;
            this.authFailed = true;
        }
    }

    private dispatch(raw: unknown): void {
        const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
        for (const item of list) {
            if (!item || typeof item !== "object") continue;
            const msg = item as Record<string, unknown>;
            if (msg.ev === "status") {
                if (msg.status === "auth_success") {
                    this.authenticated = true;
                    Array.from(this.callbacks.keys()).forEach((sym) =>
                        this.sendSub(sym),
                    );
                } else if (msg.status === "auth_failed") {
                    this.authFailed = true;
                    this.ws?.close();
                }
                continue;
            }
            if ((msg.ev === "A" || msg.ev === "AM") && msg.sym) {
                this.emit(msg as unknown as IndexAgg);
            }
        }
    }

    private emit(agg: IndexAgg): void {
        const sym = String(agg.sym).toUpperCase();
        const cbs = this.callbacks.get(sym);
        if (!cbs?.length) return;
        const tick: ChartStockLiveTick = {
            sym,
            c: agg.c,
            o: agg.o,
            h: agg.h,
            l: agg.l,
            e: agg.e ?? agg.s,
        };
        cbs.forEach((cb) => {
            try {
                cb(tick);
            } catch {
                /* ignore */
            }
        });
    }

    private sendSub(sym: string): void {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify({ action: "subscribe", params: `A.${sym}` }));
    }
}

export const polygonIndexWebSocketService = new PolygonIndexWebSocketService(
    DEFAULT_KEY,
);
